import { useCallback, useSyncExternalStore } from "react";
import { captureException } from "./sentry";

export interface BetaFeatures {
  advancedResume: boolean;
  skillGraph: boolean;
  // Pre-beta feature freeze (sw-b7h) — all default OFF, restorable via Settings.
  // See docs/feature-freeze.md for restoration runbook.
  practice: boolean;
  gamification: boolean;
  share: boolean;
  discover: boolean;
}

const STORAGE_KEY = "traitprint_beta_features";

const DEFAULTS: BetaFeatures = {
  advancedResume: false,
  skillGraph: false,
  practice: false,
  gamification: false,
  share: false,
  discover: false,
};

let listeners: Array<() => void> = [];

// Cached snapshot — stable reference between writes, satisfying useSyncExternalStore contract.
// Rebuilt only when emitChange() fires (after every write). Avoids infinite re-render loop.
let cachedSnapshot: BetaFeatures | null = null;

function buildSnapshot(): BetaFeatures {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (err) {
    void captureException(err, { tags: { module: "beta-features" } });
    return DEFAULTS;
  }
}

function emitChange() {
  cachedSnapshot = buildSnapshot();
  for (const listener of listeners) {
    listener();
  }
}

function getSnapshot(): BetaFeatures {
  if (cachedSnapshot === null) {
    cachedSnapshot = buildSnapshot();
  }
  return cachedSnapshot;
}

function subscribe(listener: () => void): () => void {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function useBetaFeatures() {
  const features = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULTS);

  const setFeature = useCallback((key: keyof BetaFeatures, value: boolean) => {
    const current = getSnapshot();
    const updated = { ...current, [key]: value };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    emitChange();
  }, []);

  return { features, setFeature };
}

/**
 * Test-only: rebuild the cached snapshot from the current localStorage value.
 * Production code should never call this — use `setFeature` instead.
 *
 * Needed because the module-level snapshot cache only refreshes on `setFeature`
 * writes; tests that mutate localStorage directly must trigger a rebuild so
 * `useSyncExternalStore` consumers re-read the new value.
 */
export function _resetBetaFeaturesCacheForTests(): void {
  cachedSnapshot = null;
  emitChange();
}
